import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PublicLayout } from "@/components/Navbar";
import { ProductCard } from "@/components/ProductCard";
import { ArrowRight, ShieldCheck, Zap, RefreshCw, Lock } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/public/products").then(({ data }) => setProducts(data.slice(0, 4))); }, []);

  const features = [
    { icon: Zap, title: "Real-Time Stock", body: "Availability is validated live against Sage 50 the moment you order — never a stale nightly import." },
    { icon: Lock, title: "Protected Trade Pricing", body: "Wholesale pricing and exact quantities are visible only to approved trade partners." },
    { icon: ShieldCheck, title: "Reserved on Order", body: "Submit an order and stock is reserved instantly, preventing oversell across retailers." },
    { icon: RefreshCw, title: "Sage-Verified", body: "Every sales order is mapped to a Sage reference with automatic retry and reconciliation." },
  ];

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative mc-grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/70 via-[#0B0C0E]/85 to-[#0B0C0E]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-36">
          <div className="max-w-3xl mc-rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              Importer & Wholesale Distributor — Ireland
            </span>
            <h1 className="mt-6 font-serif-display text-4xl font-bold leading-[1.05] text-slate-50 sm:text-5xl lg:text-6xl">
              World foods, delivered with <span className="mc-gold-gradient">live warehouse certainty.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#B6BECD]">
              The same catalogue powers our public range and our approved B2B ordering portal — where trade pricing
              and real-time, Sage-verified stock are unlocked for your account.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/apply" data-testid="hero-apply-btn" className="mc-btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base">
                Apply for Trade Account <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/catalog" data-testid="hero-catalog-btn" className="inline-flex items-center gap-2 rounded-full border border-[#2A2E39] px-7 py-3.5 text-base font-semibold text-slate-100 transition-colors hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
                Browse Products
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <div key={f.title} className="mc-card rounded-2xl p-6 mc-rise" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-100">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#94A3B8]">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <h2 className="font-serif-display text-3xl font-semibold text-slate-100 lg:text-4xl">Featured Range</h2>
            <p className="mt-2 text-sm text-[#94A3B8]">A glimpse of our wholesale catalogue. Prices unlock with a trade account.</p>
          </div>
          <Link to="/catalog" className="hidden items-center gap-1.5 text-sm font-semibold text-[#D4AF37] hover:gap-2.5 sm:inline-flex transition-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p) => (
            <ProductCard key={p.sku} product={p} to={`/product/${p.sku}`}
              priceSlot={<div className="mt-3 rounded-lg border border-[#2A2E39] bg-[#0B0C0E]/60 px-3 py-2 text-xs text-[#94A3B8]">Trade price available on account</div>}>
              <Link to={`/product/${p.sku}`} data-testid={`view-product-${p.sku}`}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-[#D4AF37]/40 px-4 py-2.5 text-sm font-semibold text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10">
                View Product
              </Link>
            </ProductCard>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
